// @flow
/**
 * Single source of truth for "can this household member become the Head of
 * Household". Used both to decide whether the Change Head of Household
 * button is enabled (is there anyone else eligible?) and to build the list
 * of selectable candidates in the modal — intentionally the same predicate
 * for both so the button's enabled state can never promise a choice the
 * candidate list doesn't actually offer.
 *
 * Excludes Minors and Prior/Pending/Denied residents (only Current adults
 * can be HoH), plus Guarantor and Live-in Caretaker (aka "Live-in Aide")
 * applicant types and any non-financially-responsible applicant type, since
 * the backend rejects those as HoH regardless.
 */

export type ApplicantTypeIds = {
  guarantorApplicantTypeId?: ?string,
  liveInCaretakerApplicantTypeId?: ?string,
  // A pre-move-in applicant is reshaped with status: 'Pending' uniformly
  // (useMiniProfileSubject.helpers.js) -- there is no "Current" status
  // pre-move-in, so the resident-only Prior/Pending/Denied exclusion below
  // would reject every applicant-stage candidate. The applicant-stage caller
  // passes this false; every existing (resident) caller keeps the default
  // and is unaffected.
  requireCurrentStatus?: boolean,
};

export const isEligibleHeadOfHouseholdCandidate = (
  occupant: ?Object,
  {
    guarantorApplicantTypeId,
    liveInCaretakerApplicantTypeId,
    requireCurrentStatus = true,
  }: ApplicantTypeIds = {},
): boolean => {
  if (!occupant) return false;
  if (occupant.type !== 'adult') return false;
  if (requireCurrentStatus && occupant.status !== 'Current') return false;
  if (!occupant.financiallyResponsible) return false;
  if (
    guarantorApplicantTypeId &&
    occupant.applicantTypeId === guarantorApplicantTypeId
  ) {
    return false;
  }
  if (
    liveInCaretakerApplicantTypeId &&
    occupant.applicantTypeId === liveInCaretakerApplicantTypeId
  ) {
    return false;
  }
  return true;
};

/**
 * Eligible adults who could become the new Head of Household — i.e. every
 * eligible occupant except whoever already holds the role today.
 */
export const getEligibleNewHeadOfHouseholdCandidates = (
  householdMembers: Array<Object> = [],
  applicantTypeIds: ApplicantTypeIds = {},
): Array<Object> =>
  (householdMembers || []).filter(
    (member) =>
      !member.isPrimary &&
      isEligibleHeadOfHouseholdCandidate(member, applicantTypeIds),
  );

export const getCurrentHeadOfHousehold = (
  householdMembers: Array<Object> = [],
): ?Object => (householdMembers || []).find((member) => member.isPrimary);
